import React, { useEffect, useState } from 'react';
import {
  Box, Stack, Typography, Chip, Paper, Select, MenuItem,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { LoadingOverlay, EmptyState, Code, GlassIconButton } from 'dashboard/components/common';

// Action keys → display labels + chip color. Anything missing renders
// with the raw key, so a new server-side action shows up immediately
// even before the UI knows about it.
const ACTION_META = {
  'user.enable':         { label: 'Enabled user',           color: 'success' },
  'user.disable':        { label: 'Disabled user',          color: 'warning' },
  'user.password_reset': { label: 'Sent password reset',    color: 'info' },
  'brand.enable':        { label: 'Enabled brand',          color: 'success' },
  'brand.disable':       { label: 'Disabled brand',         color: 'warning' },
  'sub.cancel':          { label: 'Cancelled subscription', color: 'error' },
  'sub.cancel.immediate':{ label: 'Cancelled (immediate)',  color: 'error' },
  'sub.resume':          { label: 'Resumed subscription',   color: 'info' },
  'sub.refund.full':     { label: 'Refunded (full)',        color: 'warning' },
  'sub.refund.partial':  { label: 'Refunded (partial)',     color: 'warning' },
  'domain.add':            { label: 'Added domain',          color: 'info' },
  'domain.delete':         { label: 'Deleted domain',        color: 'warning' },
  'domain.verify.success': { label: 'Domain verified',       color: 'success' },
  'domain.verify.fail':    { label: 'Domain verify failed',  color: 'warning' },
  'data.viewed.export.users':   { label: 'Exported users CSV',    color: 'default' },
  'data.viewed.export.brands':  { label: 'Exported brands CSV',   color: 'default' },
  'data.viewed.export.domains': { label: 'Exported domains CSV',  color: 'default' },
  'data.viewed.export.audit':   { label: 'Exported audit CSV',    color: 'default' },
};

const fmtTime = (ts) => (ts ? new Date(ts).toLocaleString() : '—');

const fmtMoneyMeta = (meta) => {
  if (!meta || meta.amount == null) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (meta.currency || 'usd').toUpperCase(),
    }).format(meta.amount / 100);
  } catch (_) {
    return `${(meta.amount / 100).toFixed(2)} ${(meta.currency || '').toUpperCase()}`;
  }
};

/**
 * Append-only history of every platform-admin action. Read-only — the
 * server is the source of truth, the UI doesn't let you delete rows.
 */
export default function AuditTab() {
  const [filter, setFilter] = useState('all');
  const [targetId, setTargetId] = useState(null);
  const [targetLabel, setTargetLabel] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const onExport = () => {
    setExporting(true);
    API.adminExportAudit({
      action: filter === 'all' ? undefined : filter,
      target_id: targetId || undefined,
    })
      .catch((err) => toast.error(err?.message || 'Failed to export audit log'))
      .finally(() => setExporting(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    API.adminListAudit({
      limit: 200,
      action: filter === 'all' ? undefined : filter,
      target_id: targetId || undefined,
    })
      .then((res) => { if (!cancelled) setRows(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load audit log', { toastId: 'admin-audit' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filter, targetId]);

  return (
    <Box>
      <Stack direction='row' alignItems='center' justifyContent='space-between' mb='12px' flexWrap='wrap' gap='10px'>
        <Stack direction='row' spacing='10px' alignItems='center' flexWrap='wrap'>
          <Typography variant='body2' color='text.secondary'>Action</Typography>
          <Select size='small' value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value='all'>All actions</MenuItem>
            {Object.entries(ACTION_META).map(([key, meta]) => (
              <MenuItem key={key} value={key}>{meta.label}</MenuItem>
            ))}
          </Select>
          {targetId && (
            <Chip
              size='small'
              color='primary'
              label={`Target: ${targetLabel || targetId}`}
              onDelete={() => { setTargetId(null); setTargetLabel(null); }}
            />
          )}
        </Stack>
        <Stack direction='row' spacing='10px' alignItems='center'>
          <Typography variant='caption' color='text.secondary'>
            {rows.length} entries
          </Typography>
          <GlassIconButton
            size='small'
            label='Download CSV of the current view'
            disabled={exporting}
            disabledReason={exporting ? 'Export already running — the file will download when it is ready.' : ''}
            onClick={onExport}
          >
            <DownloadIcon fontSize='small' />
          </GlassIconButton>
        </Stack>
      </Stack>

      <LoadingOverlay loading={loading} minHeight={240}>
        {rows.length === 0 && !loading ? (
          <EmptyState>No admin actions have been recorded yet.</EmptyState>
        ) : (
          <Stack spacing='8px'>
            {rows.map((e) => {
              const meta = ACTION_META[e.action] || { label: e.action, color: 'default' };
              const money = fmtMoneyMeta(e.metadata);
              return (
                <Paper
                  key={e._id}
                  elevation={0}
                  onClick={() => {
                    // Drill into "all activity on this target" by reusing
                    // the same endpoint with target_id pinned. Clicking
                    // again on the same target is a no-op.
                    if (!e.target_id || targetId === e.target_id) return;
                    setTargetId(e.target_id);
                    setTargetLabel(e.target_label || null);
                  }}
                  sx={{
                    borderRadius: '10px',
                    border: '1px solid',
                    borderColor: 'divider',
                    p: '12px',
                    cursor: e.target_id ? 'pointer' : 'default',
                    '&:hover': e.target_id ? { borderColor: 'primary.main' } : undefined,
                  }}
                >
                  <Stack direction='row' justifyContent='space-between' alignItems='flex-start' flexWrap='wrap' gap='10px'>
                    <Stack direction='row' spacing='10px' alignItems='center' sx={{ flex: 1, minWidth: 0 }}>
                      <Chip size='small' color={meta.color} label={meta.label} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant='body2' noWrap>
                          {e.target_label || e.target_id || '—'}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {e.target_type ? <Code>{e.target_type}</Code> : ''}
                          {money ? ` · ${money}` : ''}
                        </Typography>
                      </Box>
                    </Stack>
                    <Box textAlign='right'>
                      <Typography variant='caption' color='text.secondary' display='block'>
                        {fmtTime(e.createdAt)}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        by {e.actor_email || e.actor_user_id}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </LoadingOverlay>
    </Box>
  );
}
